import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Loader2, Lock, Sparkles, Video, Calendar, MessageCircle, ShieldCheck,
  ClipboardCheck, ChevronRight, Scale, Target, Award, Clock, Quote, TrendingDown, TrendingUp,
} from 'lucide-react';
import { useClientAuth } from '../../context/ClientAuthContext';
import { OnboardingCard } from './OnboardingCard';
import { OnboardingWizard } from './OnboardingWizard';
import { CoachingWorkout } from './CoachingWorkout';
import { PresentielSection } from './PresentielSection';
import { PushOptIn } from './PushOptIn';
import { LAURENT_IMAGE, CALENDLY_COACHING_URL } from '../../data/constants';

const CARD = 'relative overflow-hidden rounded-2xl bg-zinc-900/50 backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.22)]';

const fmtDateTime = (iso, en) => {
  try { return new Date(iso).toLocaleString(en ? 'en-GB' : 'fr-CH', { weekday: 'long', day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

/* ---------- Countdown to next visio ---------- */
const useCountdown = (iso) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  if (!iso) return null;
  const target = new Date(iso).getTime();
  const diff = target - now;
  if (diff <= 0) return { over: true };
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { d, h, m, s, over: false };
};

/* ---------- Coach Hero ---------- */
const CoachHero = ({ en, commitment }) => {
  const month = commitment?.month || 1;
  const total = commitment?.total || 6;
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
      className={`${CARD} p-0`} data-testid="coach-hero-card"
    >
      <div className="grid sm:grid-cols-[1.4fr_1fr]">
        <div className="p-6 sm:p-7 order-2 sm:order-1">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-pink-400">{en ? 'Your dedicated coach' : 'Votre coach dédié'}</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mt-1 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Laurent Waelti</h2>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-400">
              <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" /></span>
              {en ? 'Replies within 24h' : 'Réponse sous 24h'}
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs text-zinc-400"><ShieldCheck className="w-3.5 h-3.5 text-purple-400" /> {en ? 'Body-Up · Nyon' : 'Body-Up · Nyon'}</span>
          </div>

          <div className="mt-5">
            {commitment ? (
              <>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">{en ? 'Transformation commitment' : 'Engagement Transformation'}</span>
                  <span className="font-mono text-xs text-white font-bold">{en ? `Month ${month}/${total}` : `Mois ${month}/${total}`}</span>
                </div>
                <div className="flex gap-1.5">
                  {Array.from({ length: total }).map((_, i) => (
                    <div key={i} className={`h-1.5 flex-1 rounded-full ${i < month ? 'bg-gradient-to-r from-purple-500 to-primary' : 'bg-white/10'}`} />
                  ))}
                </div>
              </>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-400 bg-white/5 px-3 py-1.5 rounded-full" data-testid="coach-hero-no-commitment">
                {en ? 'Communauté · No commitment' : 'Communauté · Sans engagement'}
              </span>
            )}
          </div>
        </div>

        <div className="relative h-56 sm:h-auto sm:min-h-[160px] order-1 sm:order-2">
          <img src={LAURENT_IMAGE} alt="Laurent Waelti" className="absolute inset-0 w-full h-full object-cover object-[center_18%] sm:object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/10 to-transparent sm:bg-gradient-to-l sm:from-transparent sm:via-zinc-900/30 sm:to-zinc-900" />
        </div>
      </div>
    </motion.div>
  );
};

const openCalendlyPopup = (url) => {
  const open = () => window.Calendly && window.Calendly.initPopupWidget({ url });
  if (window.Calendly) { open(); return; }
  const link = document.createElement('link');
  link.rel = 'stylesheet'; link.href = 'https://assets.calendly.com/assets/external/widget.css';
  document.head.appendChild(link);
  const script = document.createElement('script');
  script.src = 'https://assets.calendly.com/assets/external/widget.js';
  script.onload = open;
  document.body.appendChild(script);
};

/* ---------- Crédits visio / séance / scan 3D ---------- */
const CreditsBar = ({ en, credits }) => {
  if (!credits) return null;
  const isVip = credits.type === 'studio_or_zoom';
  const label = isVip ? (en ? 'Studio/Zoom sessions' : 'Séances studio/Zoom') : (en ? 'Zoom calls' : 'Visios Zoom');
  return (
    <div className="rounded-xl bg-black/30 border border-white/10 p-3 mb-3" data-testid="credits-bar">
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="text-zinc-400 font-semibold">{label}</span>
        <span className="font-mono font-bold text-white">{credits.remaining}/{credits.total}</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div className="h-full rounded-full bg-gradient-to-r from-purple-500 to-primary" style={{ width: `${(credits.remaining / Math.max(1, credits.total)) * 100}%` }} />
      </div>
      {isVip && (
        <div className="flex items-center justify-between text-xs mt-2.5">
          <span className="text-zinc-400 font-semibold">{en ? '3D scans' : 'Scans 3D'}</span>
          <span className="font-mono font-bold text-white">{credits.scan3d_remaining}/{credits.scan3d_total}</span>
        </div>
      )}
      <button
        onClick={() => openCalendlyPopup(CALENDLY_COACHING_URL)}
        disabled={credits.remaining <= 0}
        className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl font-bold text-white text-sm bg-gradient-to-r from-purple-500 to-primary hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid="book-session-button"
      >
        <Calendar className="w-4 h-4" /> {credits.remaining > 0 ? (en ? 'Book my next session' : 'Réserver ma prochaine séance') : (en ? 'No credits left' : 'Plus de crédit disponible')}
      </button>
    </div>
  );
};

/* ---------- Next Visio ---------- */
const NextVisio = ({ en, visio, credits }) => {
  const scheduled = visio?.scheduled_at;
  const cd = useCountdown(scheduled);
  const upcoming = scheduled && cd && !cd.over;
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
      className={`${CARD} p-5`} data-testid="visio-card"
    >
      <div className="absolute -top-10 -right-10 w-32 h-32 bg-purple-500/20 rounded-full blur-3xl" />
      <h3 className="relative flex items-center gap-2 font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
        <Video className="w-4 h-4 text-purple-300" /> {en ? 'Next video call' : 'Prochaine visio'}
      </h3>
      <CreditsBar en={en} credits={credits} />
      {upcoming ? (
        <div className="relative">
          <p className="flex items-center gap-2 text-white font-semibold capitalize text-sm"><Calendar className="w-4 h-4 text-purple-300" /> {fmtDateTime(scheduled, en)}</p>
          <div className="grid grid-cols-4 gap-2 mt-4">
            {[[cd.d, en ? 'd' : 'j'], [cd.h, 'h'], [cd.m, 'm'], [cd.s, 's']].map(([v, l], i) => (
              <div key={i} className="rounded-xl bg-black/40 border border-white/10 py-2 text-center">
                <div className="font-mono text-xl font-bold text-white tabular-nums">{String(v).padStart(2, '0')}</div>
                <div className="font-mono text-[9px] uppercase text-zinc-500">{l}</div>
              </div>
            ))}
          </div>
          {visio.note && <p className="text-xs text-zinc-400 mt-3">{visio.note}</p>}
          {visio.zoom_url && (
            <a href={visio.zoom_url} target="_blank" rel="noreferrer"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-bold text-white bg-gradient-to-r from-purple-500 to-primary hover:scale-[1.02] active:scale-95 transition-transform shadow-lg shadow-purple-500/20" data-testid="join-visio-button">
              <Video className="w-4 h-4" /> {en ? 'Join the call' : 'Rejoindre la visio'}
            </a>
          )}
        </div>
      ) : (
        <div className="relative text-center py-6" data-testid="visio-empty">
          <Clock className="w-8 h-8 mx-auto mb-2 text-zinc-700" />
          <p className="text-sm text-zinc-400">{en ? 'No call scheduled yet. Laurent will set one up soon.' : 'Aucune visio planifiée. Laurent en fixera une prochainement.'}</p>
        </div>
      )}
    </motion.div>
  );
};

/* ---------- Chat preview ---------- */
const ChatPreview = ({ en, lastMessage, unread, onOpenChat }) => (
  <motion.div
    initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
    className={`${CARD} p-5 flex flex-col`} data-testid="chat-preview-card"
  >
    <h3 className="flex items-center justify-between font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
      <span className="flex items-center gap-2"><MessageCircle className="w-4 h-4 text-primary" /> {en ? '24/7 Messaging' : 'Messagerie 24/7'}</span>
      {unread > 0 && <span className="text-[10px] font-bold text-white bg-primary rounded-full px-2 py-0.5">{unread}</span>}
    </h3>
    <div className="flex-1">
      {lastMessage ? (
        <div className={`rounded-2xl px-4 py-3 text-sm ${lastMessage.sender === 'coach' ? 'bg-zinc-800 text-zinc-100 border border-white/10' : 'bg-primary/10 text-zinc-200 border border-primary/20'}`}>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{lastMessage.sender === 'coach' ? 'Coach Laurent' : (en ? 'You' : 'Vous')}</p>
          <p className="line-clamp-2">{lastMessage.text}</p>
        </div>
      ) : (
        <p className="text-sm text-zinc-500 py-3">{en ? 'Start the conversation with your coach 👋' : 'Démarrez la conversation avec votre coach 👋'}</p>
      )}
    </div>
    <button onClick={onOpenChat} className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.02] active:scale-95 transition-transform" data-testid="open-chat-button">
      {en ? 'Open the chat' : 'Ouvrir la messagerie'} <ChevronRight className="w-4 h-4" />
    </button>
    <div className="mt-3 flex justify-center"><PushOptIn en={en} /></div>
  </motion.div>
);

/* ---------- Weight sparkline (30d) ---------- */
const Sparkline = ({ series, en }) => {
  if (!series || series.length < 2) {
    return <p className="text-[11px] text-zinc-500 mt-3">{en ? 'Log your weight to see your 30-day trend.' : 'Notez votre poids pour voir votre tendance sur 30 jours.'}</p>;
  }
  const ws = series.map((p) => p.weight);
  const min = Math.min(...ws), max = Math.max(...ws);
  const span = max - min || 1;
  const w = 240, h = 44, pad = 4;
  const pts = series.map((p, i) => {
    const x = pad + (i * (w - pad * 2)) / (series.length - 1);
    const y = pad + (h - pad * 2) * (1 - (p.weight - min) / span);
    return [x, y];
  });
  const d = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
  const delta = +(ws[ws.length - 1] - ws[0]).toFixed(1);
  const down = delta < 0;
  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-1">
        <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{en ? 'Weight · 30 days' : 'Poids · 30 jours'}</span>
        <span className={`inline-flex items-center gap-1 text-[11px] font-bold ${down ? 'text-emerald-400' : delta > 0 ? 'text-pink-400' : 'text-zinc-400'}`}>
          {down ? <TrendingDown className="w-3 h-3" /> : delta > 0 ? <TrendingUp className="w-3 h-3" /> : null}
          {delta > 0 ? '+' : ''}{delta} kg
        </span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-11" preserveAspectRatio="none">
        <defs><linearGradient id="spark" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#a855f7" /><stop offset="100%" stopColor="#ec4899" /></linearGradient></defs>
        <path d={d} fill="none" stroke="url(#spark)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="3" fill="#ec4899" />
      </svg>
    </div>
  );
};

/* ---------- Monthly check-in ---------- */
const MonthlyCheckin = ({ en, stats, onOpenTracking }) => {
  const adherence = stats?.adherence;
  const weight = stats?.weight;
  const days = stats?.days_logged ?? 0;
  const r = 26, circ = 2 * Math.PI * r;
  const pct = adherence ?? 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
      className={`${CARD} p-5 flex flex-col`} data-testid="monthly-checkin-card"
    >
      <h3 className="flex items-center gap-2 font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}><Award className="w-4 h-4 text-yellow-400" /> {en ? 'My check-in' : 'Mon bilan'}</h3>
      <div className="flex items-center gap-4">
        <div className="relative w-[68px] h-[68px] shrink-0">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 64 64">
            <circle cx="32" cy="32" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
            <circle cx="32" cy="32" r={r} fill="none" stroke="url(#cg)" strokeWidth="6" strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={circ * (1 - pct / 100)} />
            <defs><linearGradient id="cg" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#a855f7" /><stop offset="100%" stopColor="#ec4899" /></linearGradient></defs>
          </svg>
          <div className="absolute inset-0 flex items-center justify-center font-mono font-bold text-white text-sm">{adherence != null ? `${adherence}%` : '—'}</div>
        </div>
        <div className="space-y-1.5 text-sm">
          <p className="flex items-center gap-1.5 text-zinc-300"><Target className="w-3.5 h-3.5 text-purple-400" /> {en ? 'Calorie adherence' : 'Adhérence calories'}</p>
          <p className="flex items-center gap-1.5 text-zinc-300"><Scale className="w-3.5 h-3.5 text-primary" /> {weight != null ? `${weight} kg` : (en ? 'No weight yet' : 'Pas de poids')}</p>
          <p className="text-xs text-zinc-500">{en ? `${days}/7 days logged` : `${days}/7 jours suivis`}</p>
        </div>
      </div>
      <Sparkline series={stats?.weight_series} en={en} />
      <button onClick={onOpenTracking} className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white border border-white/15 hover:border-primary/50 transition-colors" data-testid="open-tracking-button">
        {en ? 'Open my tracking' : 'Ouvrir mon suivi'} <ChevronRight className="w-4 h-4" />
      </button>
    </motion.div>
  );
};

/* ---------- Coach message (AI, lazy) ---------- */
const CoachMessage = ({ en, api }) => {
  const [msg, setMsg] = useState(undefined);
  useEffect(() => {
    let active = true;
    api(`/api/tracking/coaching/checkin-message?lang=${en ? 'en' : 'fr'}`)
      .then((d) => { if (active) setMsg(d.message || null); })
      .catch(() => { if (active) setMsg(null); });
    return () => { active = false; };
  }, [api, en]);

  if (msg === null) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
      className={`${CARD} p-5`} data-testid="coach-message-card"
    >
      <div className="absolute -top-8 -left-8 w-28 h-28 bg-primary/15 rounded-full blur-3xl" />
      <div className="relative flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-primary flex items-center justify-center shrink-0">
          <Quote className="w-4 h-4 text-white" />
        </div>
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{en ? 'A word from your coach' : 'Le mot de votre coach'}</p>
          {msg === undefined ? (
            <div className="flex items-center gap-2 text-zinc-500 text-sm"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {en ? 'Laurent is writing…' : 'Laurent rédige…'}</div>
          ) : (
            <p className="text-white text-[15px] leading-relaxed">{msg}<span className="block text-sm text-pink-400 font-semibold mt-1.5">— Laurent</span></p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

/* ---------- Hub ---------- */
export const CoachingHub = ({ en, onNavigate }) => {
  const { authFetch } = useClientAuth();
  const api = useCallback((p, o) => authFetch(p, o).then((r) => (r.ok ? r.json() : Promise.reject(r))), [authFetch]);
  const [data, setData] = useState(null);
  const [skipWizard, setSkipWizard] = useState(() => sessionStorage.getItem('ob_wizard_skip') === '1');
  const nav = useNavigate();

  const load = useCallback(() => {
    api('/api/tracking/coaching').then(setData).catch(() => setData({ transformation: false }));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  if (data.transformation && !data.onboarding?.completed_at && !skipWizard) {
    return (
      <OnboardingWizard
        en={en} api={api} onDone={load}
        onSkip={() => { sessionStorage.setItem('ob_wizard_skip', '1'); setSkipWizard(true); }}
      />
    );
  }

  if (!data.transformation) {
    return (
      <div className="relative" data-testid="coaching-locked">
        <div className="rounded-3xl border border-purple-500/30 bg-gradient-to-br from-purple-500/10 to-black p-10 text-center max-w-lg mx-auto">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500/25 to-primary/20 flex items-center justify-center mx-auto mb-4"><Lock className="w-7 h-7 text-purple-300" /></div>
          <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{en ? 'Online coaching with Laurent' : 'Coaching en ligne avec Laurent'}</h3>
          <p className="text-zinc-400 mt-2 text-sm">{en ? 'Onboarding, monthly video calls, personalized training programs and 24/7 messaging — all in the Transformation pack.' : 'Onboarding, visio mensuelle, programmes de musculation personnalisés et messagerie 24/7 — tout est inclus dans le pack Transformation.'}</p>
          <button onClick={() => nav('/transformation')} className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-purple-500 to-primary hover:scale-[1.02] active:scale-95 transition-transform" data-testid="premium-upsell-button">
            <Sparkles className="w-4 h-4" /> {en ? 'Discover the Transformation pack' : 'Découvrir le pack Transformation'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="coaching-hub">
      <CoachHero en={en} commitment={data.commitment} />

      <div className="grid lg:grid-cols-3 gap-6">
        <NextVisio en={en} visio={data.visio} credits={data.credits} />
        <ChatPreview en={en} lastMessage={data.last_message} unread={data.unread || 0} onOpenChat={() => (onNavigate ? onNavigate('social', 'coach') : nav('/mon-espace'))} />
        <MonthlyCheckin en={en} stats={data.stats} onOpenTracking={() => onNavigate && onNavigate('tracking')} />
      </div>

      <CoachMessage en={en} api={api} />

      <PresentielSection en={en} api={api} />

      <CoachingWorkout en={en} onOpenProgram={(programId) => onNavigate && onNavigate('workouts', programId)} />

      <div>
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-pink-500 flex items-center justify-center shrink-0">
            <ClipboardCheck className="w-4.5 h-4.5 text-white" />
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{en ? 'Your profile' : 'Votre profil'}</p>
            <h3 className="font-bold text-white text-lg leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{en ? 'Onboarding' : 'Onboarding'}</h3>
          </div>
        </div>
        <OnboardingCard en={en} onboarding={data.onboarding} api={api} onSaved={load} />
      </div>
    </div>
  );
};

export default CoachingHub;
